import type { Theme } from '@mui/material/styles'
import { alpha, lighten } from '@mui/material/styles'

/**
 * Switch « Fil », le seul interrupteur de l'app : contour de 1 px et pastille en pilule.
 * Désactivé, contour et pastille gris ; activé, bleus sur un fond à peine teinté.
 * Lu par `TraditionalSwitch` et par l'override `MuiSwitch` du thème : les deux restent identiques.
 */
export const FIL_SWITCH_SIZES = {
  medium: { trackW: 36, trackH: 18, thumbW: 17, thumbH: 12 },
  small: { trackW: 30, trackH: 16, thumbW: 14, thumbH: 10 },
} as const

export type FilSwitchSize = keyof typeof FIL_SWITCH_SIZES

export const getFilSwitchGeometry = (size: FilSwitchSize) => {
  const { trackW, trackH, thumbW, thumbH } = FIL_SWITCH_SIZES[size]
  const pad = (trackH - thumbH) / 2
  return {
    trackW,
    trackH,
    thumbW,
    thumbH,
    pad,
    travel: trackW - thumbW - 2 * pad,
  }
}

export const getFilSwitchColors = (theme: Theme) => {
  const dark = theme.palette.mode === 'dark'
  const { primary } = theme.palette
  // Hors du thème Nabu (tests sans ThemeProvider), `neutral` manque : les gris MUI prennent le relais.
  const neutral = theme.palette.neutral ?? theme.palette.grey
  return {
    line: dark ? neutral[500] : neutral[400],
    lineHover: dark ? neutral[400] : neutral[600],
    // En sombre, le bleu de base manque de contraste sur le fond : une nuance éclaircie se lit.
    accent: dark ? lighten(primary.main, 0.4) : primary.main,
    accentHover: dark ? lighten(primary.main, 0.55) : primary.dark,
    fill: alpha(primary.main, dark ? 0.22 : 0.1),
    focusRing: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${alpha(
      primary.main,
      0.55
    )}`,
  }
}

export const filSwitchInset = (color: string) => `inset 0 0 0 1px ${color}`

export const FIL_SWITCH_DISABLED_OPACITY = 0.4
